'use client';
import React, { useState } from 'react';
import { Preset } from '@/lib/usePresets';

interface PresetsPanelProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  presets: Preset<any>[];
  onSave: (name: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLoad: (settings: any) => void;
  onDelete: (id: string) => void;
  accentColor?: string;
}

export default function PresetsPanel({
  presets,
  onSave,
  onLoad,
  onDelete,
  accentColor = 'amber',
}: PresetsPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const accent = accentColor;

  const handleSave = () => {
    const name = newName.trim();
    if (!name) return;
    onSave(name);
    setNewName('');
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const handleDelete = (id: string) => {
    if (deleteConfirmId === id) {
      onDelete(id);
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className={`w-4 h-4 text-${accent}-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <span className="text-sm font-semibold text-gray-700">My Presets</span>
          {presets.length > 0 && (
            <span className={`text-xs bg-${accent}-100 text-${accent}-700 rounded-full px-2 py-0.5 font-medium`}>
              {presets.length}
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Save current settings */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Save Current Settings</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="Preset name…"
                maxLength={40}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={!newName.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  newName.trim()
                    ? `bg-${accent}-500 hover:bg-${accent}-600 text-white`
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {savedFlash ? '✓ Saved' : 'Save'}
              </button>
            </div>
          </div>

          {/* Saved presets list */}
          {presets.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Saved Presets</p>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {presets.map(preset => (
                  <div
                    key={preset.id}
                    className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5 group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{preset.name}</p>
                      <p className="text-xs text-gray-400">{formatDate(preset.createdAt)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onLoad(preset.settings)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md bg-${accent}-100 text-${accent}-700 hover:bg-${accent}-200 transition-colors shrink-0`}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(preset.id)}
                      title={deleteConfirmId === preset.id ? 'Click again to confirm' : 'Delete preset'}
                      className={`p-1.5 rounded-md transition-colors shrink-0 ${
                        deleteConfirmId === preset.id
                          ? 'bg-red-100 text-red-600' :'text-gray-300 hover:text-red-400 hover:bg-red-50'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 text-sm">
              <svg className="w-8 h-8 mx-auto mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              No presets saved yet. Configure your settings and save them above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
